import { useFormStore } from "@/stores/use-form-store"
import FieldInfoIcon from "../../components/field-info"
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "../../components/ui/form"
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group"
import { cn, parseAttributes } from "../../lib/utils"
import {
	RadioConfig,
	type Attribute,
	type FieldProps,
} from "../../types/form-types"
import { Description } from "./components/description"
import { useLogicStore } from "@/stores/use-conditional-logic-store"
import { ErrorMessage } from "./components/error-message"

export const RadioField = ({ form, field, commonAttributes }: FieldProps) => {
	const { options, displayStyle } = getRadioInputAttributes(field.attributes)
	const { label, infoIconText, infoIconType, description, infoIconRequired } =
		commonAttributes
	const { shouldDisable, verifiedFields } = useFormStore()

	const isVerified = verifiedFields.get(field.field_id) || false

	const isHorizontal = displayStyle === "HORIZONTAL"
	const { hiddenFields, errors } = useLogicStore()

	if (hiddenFields[field.field_id]) return null

	return (
		<FormField
			control={form.control}
			name={field.field_id}
			render={({ field: formField }) => (
				<FormItem className="space-y-3">
					<FormLabel>
						<div className="flex flex-col gap-1">
							<div className="flex items-center gap-1">
								<p
									className={cn(
										field.is_required &&
											"after:ml-1 after:text-red-500 after:content-['*']",
									)}
								>
									{label}
								</p>
								{infoIconRequired && (
									<FieldInfoIcon
										infoText={infoIconText}
										iconType={infoIconType}
									/>
								)}
							</div>
							{description && (
								<Description description={description} />
							)}
						</div>
					</FormLabel>
					<FormControl>
						<RadioGroup
							value={formField.value}
							onValueChange={formField.onChange}
							defaultValue={formField.value}
							className={cn(
								"flex flex-col",
								isHorizontal
									? "grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3"
									: "",
							)}
						>
							{options?.map(option => (
								<FormItem className="flex items-center gap-3">
									<FormControl>
										<RadioGroupItem
											disabled={
												isVerified || shouldDisable
											}
											value={option.value}
											checked={
												option.value === formField.value
											}
										/>
									</FormControl>
									<FormLabel className="font-normal">
										{option.label}
									</FormLabel>
								</FormItem>
							))}
						</RadioGroup>
					</FormControl>
					<FormMessage />
					<ErrorMessage error={errors[field.field_id]} />
				</FormItem>
			)}
		/>
	)
}

function getRadioInputAttributes(attributes: Attribute[]) {
	const props: {
		options?: { label: string; value: string }[]
		defaultOption?: string
		displayStyle?: string
	} = {}

	const parsedAttributes = parseAttributes(attributes)

	Object.entries(parsedAttributes).forEach(([key, value]) => {
		switch (key) {
			case RadioConfig.Options:
				props.options = value as { label: string; value: string }[]
				break
			case RadioConfig.DisplayStyle:
				props.displayStyle = value as string
				break
			case RadioConfig.DefaultOption:
				props.defaultOption = value as string
				break
			default:
				break
		}
	})
	return props
}
